/**
 * Low-level HTTP request primitive for saas-admin.
 *
 * Responsibilities (mirroring saas_login/src/api/client.ts but adapted for
 * the admin surface):
 *
 *  - Base URL resolution: auth base for `/api/auth/**`, admin base for
 *    `/api/saas-admin/**`.
 *  - `withCredentials: true` on every call (axios equivalent of the prior
 *    `credentials:'include'`).
 *  - `X-Request-ID` for traceability.
 *  - CSRF double-submit on every path the shipped `CsrfDoubleSubmitFilter`
 *    protects: `/api/auth/refresh`, `/api/auth/logout`, `/api/account/**`,
 *    AND `/api/saas-admin/**` on POST/PUT/DELETE/PATCH (specs/0003).
 *    Refresh/logout/account read the token via `csrf.ts` (cookie-first,
 *    GET /api/auth/csrf fallback). `/api/saas-admin/**` mutations read the
 *    token from `authStore.csrfToken`, populated from the refresh response
 *    body — the `journix_csrf` cookie is cross-origin at saas-admin.journix.ai
 *    and unreadable from JS, so the body is the authoritative source.
 *  - Bearer header attached to `/api/saas-admin/**` and `/api/account/**`.
 *  - 401 → one silent refresh → retry the original call once. Bounded.
 *  - 403 `INVALID_CSRF` on `/api/saas-admin/**` mutations → one silent
 *    refresh (which captures a fresh csrfToken) → retry the original call
 *    once. Strictly NOT triggered by any other 403 code.
 *  - Error normalization through ApiError.
 *
 * Transport is axios (see `httpClient` below) rather than `fetch`; the
 * `request<T>()` public surface and all of the behavior above are unchanged.
 * The axios instance is configured to NEVER throw on a non-2xx status
 * (`validateStatus: () => true`) and to return the raw response body as text
 * (`transformResponse` identity + `responseType: 'text'`) so the status- and
 * body-handling logic below stays byte-identical to the previous fetch path.
 */
import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { ApiError, apiErrorFromEnvelope, type ErrorCode } from './errors';
import { fetchCsrfToken } from './csrf';
import { generateRequestId } from '@/utils/uuid';
import { env } from '@/config/env';
import {
  getAccessToken,
  getCsrfToken,
  setAccessToken,
  setCsrfToken,
  signOut,
} from '@/auth/authStore';
import type { ApiResponse, AuthTokenResponse } from './types';

const CSRF_PROTECTED_EXACT = new Set<string>(['/api/auth/refresh', '/api/auth/logout']);
const CSRF_PROTECTED_PREFIXES: readonly string[] = ['/api/account/'];

/**
 * Refresh/logout/account paths: read the CSRF token via csrf.ts (cookie or
 * GET /api/auth/csrf fallback) and echo it in BOTH the X-CSRF-Token header
 * AND the body's `csrfToken` field (the bootstrap convention).
 */
function isCsrfProtected(path: string): boolean {
  if (CSRF_PROTECTED_EXACT.has(path)) return true;
  return CSRF_PROTECTED_PREFIXES.some((p) => path.startsWith(p));
}

function isRefreshPath(path: string): boolean {
  return path === '/api/auth/refresh';
}

function isBearerPath(path: string): boolean {
  return (
    path.startsWith('/api/saas-admin/') ||
    path.startsWith('/api/account/') ||
    path === '/api/auth/me'
  );
}

/**
 * `/api/saas-admin/**` mutations: header-only double-submit. The token is
 * read from `authStore.csrfToken` (populated from the refresh response
 * body). No body-field echo — the shipped CsrfDoubleSubmitFilter validates
 * header-vs-cookie only on this surface; a body field would be ignored.
 */
function isAdminMutationPath(path: string, method: string): boolean {
  if (method === 'GET' || method === 'HEAD' || method === 'OPTIONS') return false;
  return path.startsWith('/api/saas-admin/');
}

function baseUrlFor(path: string): string {
  if (path.startsWith('/api/saas-admin/')) return env.adminApiBaseUrl;
  return env.authBaseUrl;
}

/**
 * Per-call metadata threaded onto the axios request config so the request
 * interceptor can inject the bearer + admin-CSRF headers off the original
 * `path`/`method` (not the resolved URL). Lives under a private key so it
 * never collides with axios' own config fields.
 */
interface RequestMeta {
  path: string;
  method: string;
}

interface AdminRequestConfig extends InternalAxiosRequestConfig {
  __meta?: RequestMeta;
}

/**
 * Shared axios instance. `withCredentials` carries cookies on every call
 * (auth invariant #2). `validateStatus` is disabled so a non-2xx status
 * resolves rather than throwing — the 401/403 retry logic and `parseResponse`
 * below own all status handling. `transformResponse` is the identity so the
 * body arrives as a raw string and the JSON/non-JSON parsing matches the
 * prior fetch path exactly.
 */
export const httpClient: AxiosInstance = axios.create({
  withCredentials: true,
  validateStatus: () => true,
  responseType: 'text',
  transformResponse: [(data: unknown) => data],
});

// Request interceptor: bearer injection (bearer paths) + admin-mutation CSRF
// header (token from authStore). Accept/X-Request-ID and the
// refresh/logout/account CSRF header+body are set in `request()` because they
// depend on the per-call request id and the JSON body merge.
httpClient.interceptors.request.use((config: AdminRequestConfig) => {
  const meta = config.__meta;
  if (!meta) return config;
  const { path, method } = meta;

  if (isBearerPath(path)) {
    const token = getAccessToken();
    if (token) config.headers.set('Authorization', `Bearer ${token}`);
  }

  // Admin mutations: header-only double-submit from the authStore stash. If the
  // stash is empty (no refresh yet) we still issue the call — the backend 403s
  // INVALID_CSRF, which the retry-once block in `request()` resolves.
  if (isAdminMutationPath(path, method)) {
    const adminCsrf = getCsrfToken();
    if (adminCsrf) config.headers.set('X-CSRF-Token', adminCsrf);
  }

  return config;
});

let refreshInFlight: Promise<boolean> | null = null;

export function __resetClientState(): void {
  refreshInFlight = null;
}

async function silentRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const body = await request<AuthTokenResponse>('POST', '/api/auth/refresh', {
        json: {},
        _isRefresh: true,
      });
      if (body.accessToken) {
        setAccessToken(body.accessToken, body.expiresAt ?? null);
        // Capture csrfToken from the FLAT response body (no .data wrapper).
        // Authoritative for /api/saas-admin/** mutations — see specs/0003.
        setCsrfToken(body.csrfToken ?? null);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  })().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

export interface RequestOptions {
  json?: unknown;
  formData?: FormData;
  query?: Record<string, string | number | undefined>;
  headers?: Record<string, string>;
  _isRefresh?: boolean;
  _isRetry?: boolean;
  /**
   * Opt a bearer call out of the 401 → silent-refresh → retry path
   * (SASADM-0081 U3). `GET /api/auth/me` sets this so a `401` (the pre-0133
   * reality for a PLATFORM principal) fails soft — no `silentRefresh()`, no
   * `signOut()` — and the caller keeps the claims-derived identity.
   */
  _noAuthRetry?: boolean;
}

export async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const baseUrl = baseUrlFor(path);
  const url = buildUrl(baseUrl, path, options.query);
  const requestId = generateRequestId();

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Request-ID': requestId,
    ...options.headers,
  };

  if (options.json !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  // When sending formData, do NOT set Content-Type — the browser sets
  // multipart/form-data with the correct boundary automatically.

  // Bearer (bearer paths) and the admin-mutation CSRF header are attached by
  // the request interceptor off `__meta` below.

  // CSRF double-submit (refresh/logout/account): header + body field, token
  // sourced from csrf.ts (cookie-first; GET /api/auth/csrf fallback).
  let csrfValue: string | undefined;
  const needsCsrf = method !== 'GET' && isCsrfProtected(path);
  if (needsCsrf) {
    csrfValue = await fetchCsrfToken(baseUrl);
    headers['X-CSRF-Token'] = csrfValue;
  }

  // The admin-mutation CSRF header is injected by the request interceptor
  // (header-only, token sourced from authStore). Disjoint from `needsCsrf`
  // because isCsrfProtected() returns false for /api/saas-admin/**.

  // Build the request body, mirroring the previous fetch path exactly.
  let data: FormData | string | undefined;
  if (options.formData !== undefined) {
    // Multipart body: pass FormData directly; do NOT stringify or set
    // Content-Type. Bearer auth and CSRF header (for admin mutations) continue
    // to apply — they key off path/method, not body format. Strip any
    // Content-Type the caller may have set so axios/the browser computes the
    // multipart boundary (the prior fetch path never set it for FormData).
    data = options.formData;
    delete headers['Content-Type'];
  } else if (options.json !== undefined) {
    // If this is a CSRF-protected call, merge csrfToken into the body
    // (refresh/logout expect it per contract §2.7.5 / §2.7.9).
    let payload: unknown = options.json;
    if (needsCsrf && csrfValue && typeof payload === 'object' && payload !== null) {
      payload = { ...(payload as Record<string, unknown>), csrfToken: csrfValue };
    } else if (needsCsrf && csrfValue && options.json === undefined) {
      payload = { csrfToken: csrfValue };
    }
    data = JSON.stringify(payload);
  } else if (needsCsrf && csrfValue) {
    headers['Content-Type'] = 'application/json';
    data = JSON.stringify({ csrfToken: csrfValue });
  }

  const meta: RequestMeta = { path, method };

  // Admin mutations need X-CSRF-Token (header-only double-submit). The historical
  // source is the refresh-response body, but console-origin refresh is origin-gated
  // (403 INVALID_ORIGIN) until SASATH-0133-A. GET /api/auth/csrf is LIVE and, in
  // every env, served on the admin API host (VITE_ADMIN_API_BASE_URL === VITE_AUTH_
  // BASE_URL), so it sets journix_csrf on that host AND returns the value — a valid
  // double-submit pair. Seed the stash when empty (before the request so the request
  // interceptor reads the freshly-stashed token); post-0133 the refresh body keeps it
  // populated and this fetch is skipped.
  if (isAdminMutationPath(path, method) && !options._isRefresh && !getCsrfToken()) {
    try {
      setCsrfToken(await fetchCsrfToken(baseUrl));
    } catch {
      // leave empty — the existing 403 INVALID_CSRF retry-once path is the backstop
    }
  }

  let res: AxiosResponse<string>;
  try {
    res = await httpClient.request<string>({
      url,
      method,
      headers,
      data,
      // Marker consumed by the request interceptor for bearer/admin-CSRF.
      __meta: meta,
    } as AdminRequestConfig);
  } catch {
    throw new ApiError('NETWORK_ERROR', 0, { requestId });
  }

  // 403 INVALID_CSRF on admin mutations → one silent refresh (which
  // re-populates authStore.csrfToken) → retry once. Strictly scoped to
  // INVALID_CSRF — any other 403 code (INVALID_ORIGIN, TENANT_ORIGIN_MISMATCH,
  // INVALID_AUDIENCE, …) propagates immediately because a refresh won't
  // help. The `_isRetry` guard prevents looping (same guarantee as the 401
  // path below).
  if (
    res.status === 403 &&
    isAdminMutationPath(path, method) &&
    !options._isRefresh &&
    !options._isRetry
  ) {
    let code: string | undefined;
    try {
      const parsed = JSON.parse(res.data ?? '') as { error?: { code?: string } };
      code = parsed.error?.code;
    } catch {
      /* not JSON; fall through */
    }
    if (code === 'INVALID_CSRF') {
      const ok = await silentRefresh();
      if (!ok) {
        signOut();
        throw new ApiError('SESSION_EXPIRED', 401, { requestId });
      }
      return request<T>(method, path, { ...options, _isRetry: true });
    }
  }

  // 401 → silent refresh → retry once. Only on bearer paths; refresh itself
  // is excluded by the guards.
  if (
    res.status === 401 &&
    isBearerPath(path) &&
    !options._isRefresh &&
    !options._isRetry &&
    !options._noAuthRetry &&
    !isRefreshPath(path)
  ) {
    const ok = await silentRefresh();
    if (!ok) {
      signOut();
      throw new ApiError('SESSION_EXPIRED', 401, { requestId });
    }
    return request<T>(method, path, { ...options, _isRetry: true });
  }

  return parseResponse<T>(res, requestId);
}

/**
 * SASADM-0159 — additive binary read (the brand theme bundle zip). Same base
 * URL, bearer (request interceptor), `withCredentials` and 401 → one silent
 * refresh → retry once as `request()`; only this call sets `responseType:
 * 'blob'`. A non-2xx body is read as text and thrown through `parseResponse`,
 * so an envelope becomes the usual ApiError and a non-JSON body stays on
 * `details.raw` (SASADM-0158). GET only: a blob mutation would also need the
 * admin CSRF seed and the 403 `INVALID_CSRF` retry.
 */
export async function requestBlob(
  method: 'GET',
  path: string,
  options: Pick<RequestOptions, '_isRetry'> = {},
): Promise<Blob> {
  const url = buildUrl(baseUrlFor(path), path);
  const requestId = generateRequestId();
  const headers: Record<string, string> = {
    Accept: 'application/zip, application/json',
    'X-Request-ID': requestId,
  };

  let res: AxiosResponse<Blob>;
  try {
    res = await httpClient.request<Blob>({
      url,
      method,
      headers,
      responseType: 'blob',
      __meta: { path, method },
    } as AdminRequestConfig);
  } catch {
    throw new ApiError('NETWORK_ERROR', 0, { requestId });
  }

  if (res.status === 401 && isBearerPath(path) && !options._isRetry) {
    const ok = await silentRefresh();
    if (!ok) {
      signOut();
      throw new ApiError('SESSION_EXPIRED', 401, { requestId });
    }
    return requestBlob(method, path, { _isRetry: true });
  }

  if (res.status >= 200 && res.status < 300) return res.data;
  const text = res.data instanceof Blob ? await blobText(res.data) : '';
  return parseResponse<never>({ ...res, data: text }, requestId);
}

/** A Blob's text; `FileReader` because jsdom's Blob has no `text()`. */
function blobText(blob: Blob): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => resolve('');
    reader.readAsText(blob);
  });
}

function parseResponse<T>(res: AxiosResponse<string>, requestId: string): T {
  const headerRequestId = res.headers['x-request-id'];
  const responseRequestId =
    typeof headerRequestId === 'string' ? headerRequestId : requestId;

  if (res.status === 204) {
    return undefined as T;
  }

  const text = typeof res.data === 'string' ? res.data : '';
  const ok = res.status >= 200 && res.status < 300;
  let body: unknown = null;
  // SASADM-0158 — a non-JSON body (an edge 502 page, a proxy's plain text) is
  // kept on `details.raw` only, never `details.message`, so callers fall back
  // to the code's copy instead of showing the raw text.
  let nonJson = false;
  if (text.length > 0) {
    try {
      body = JSON.parse(text);
    } catch {
      if (ok) {
        throw new ApiError('UNKNOWN', res.status, {
          requestId: responseRequestId,
          details: { raw: text },
        });
      }
      nonJson = true;
    }
  }

  if (!ok) {
    const error = nonJson
      ? new ApiError(statusToCode(res.status), res.status, {
          requestId: responseRequestId,
          details: { raw: text },
        })
      : apiErrorFromEnvelope(res.status, body, responseRequestId);
    // SASADM-0154 — additive: a 429 carries its `Retry-After` for callers that
    // back off and retry (the theme preview).
    if (res.status === 429) {
      const retryAfterSeconds = parseRetryAfter(res.headers['retry-after']);
      if (retryAfterSeconds !== undefined) error.details.retryAfterSeconds = retryAfterSeconds;
    }
    throw error;
  }
  return body as T;
}

/**
 * SASADM-0157 — the back-off, in seconds, when a 429 carries no readable
 * `Retry-After` (saas-auth: "treat an unreadable Retry-After as 'wait 60 s'"
 * until SASATH-0349 exposes the header). Shared by the brand Theme and
 * Translations tabs so the two cannot drift.
 */
export const DEFAULT_RETRY_AFTER_SECONDS = 60;

/** `Retry-After` as whole seconds — delta-seconds or an HTTP date; undefined when absent/unparseable. */
function parseRetryAfter(header: unknown): number | undefined {
  if (typeof header !== 'string' || header.trim() === '') return undefined;
  const seconds = Number(header.trim());
  if (Number.isFinite(seconds)) return Math.max(0, Math.ceil(seconds));
  const at = Date.parse(header);
  if (Number.isNaN(at)) return undefined;
  return Math.max(0, Math.ceil((at - Date.now()) / 1000));
}

function statusToCode(status: number): ErrorCode {
  if (status === 401) return 'SESSION_EXPIRED';
  if (status === 403) return 'TENANT_ORIGIN_MISMATCH';
  if (status === 429) return 'RATE_LIMIT_REJECTED';
  if (status === 502 || status === 503 || status === 504) return 'SERVICE_UNAVAILABLE';
  return 'UNKNOWN';
}

function buildUrl(
  baseUrl: string,
  path: string,
  query?: Record<string, string | number | undefined>,
): string {
  const url = new URL(path, baseUrl + '/');
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined) url.searchParams.append(k, String(v));
    }
  }
  return url.toString();
}

/**
 * SASATH-0212 — collapse a paging/filter param bag into the `query` record
 * `request()` accepts, dropping everything the server should never see:
 * `undefined`, the empty string (an empty `q` must not be sent at all), and
 * `false` (the flags are opt-in). `0` and other falsy numbers survive, so
 * `page: 0` is still sent. Surviving booleans serialize as `'true'`.
 *
 * Values are passed through verbatim — `buildUrl` URL-encodes them. In
 * particular `q` is NOT sanitized: `%`, `_` and `\` are matched literally by
 * the server, so escaping them client-side would corrupt the search.
 */
export function toQuery(
  params: Record<string, string | number | boolean | undefined>,
): Record<string, string | number | undefined> {
  const out: Record<string, string | number | undefined> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '' || value === false) continue;
    out[key] = value === true ? 'true' : value;
  }
  return out;
}

/**
 * Unwrap the standard ApiResponse<T> envelope. Admin endpoints all return
 * `{ success, message, data }`; callers want the data.
 */
export function unwrap<T>(response: ApiResponse<T>): T {
  return response.data;
}
